// v7.1 — accessible tooltip used by every form control in ControlsPanel.
//
// Behaviour:
//   - Renders on hover AND on keyboard focus (Tab to reveal).
//   - Falls back to the native `title` attribute for AT users who can't
//     perceive hover/focus tooltips.
//   - Positions itself with `position="top"` (default) or `"right"`,
//     auto-flipping if there's not enough room (we keep the implementation
//     simple and always use top with a small offset).
//   - The tooltip content is a single React node; it can be plain text
//     (the most common case) or richer markup (e.g. two-line label +
//     description).
//
// v7.1 fix — the popup was previously nested inside the inline-flex wrapper
// and constrained to the trigger's width, which made long tooltips wrap
// word-per-line. We now portal the popup to document.body and compute its
// position with useLayoutEffect against the trigger's bounding rect so the
// popup can be a fixed 200–260 px block, centred above the trigger.
//
// Accessibility:
//   - The trigger element exposes the full tooltip text via
//     `aria-describedby` so screen readers read it after the field's
//     accessible name.
//   - Tooltip itself uses `role="tooltip"` so SR-only users hear it as
//     a description, not new content.

import {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { pick, useLanguage } from '../lib/language';

export type TooltipValue = string | { plain: string; technical: string };

export interface TooltipProps {
  /** The trigger — usually a label or button. */
  children: ReactNode;
  /** Plain string (used in both modes) or a per-mode pair. */
  value: TooltipValue;
  /** When true, render on the right (used for short labels). */
  side?: 'top' | 'right';
  /** Optional max width for the popup (px). Default 260. */
  maxWidth?: number;
  /**
   * When `true`, the tooltip is wrapped around a label/heading element
   * and uses a `tabIndex={0}` div so screen readers treat the whole
   * label as a single describable focusable unit. Default true — most
   * controls in this app already have their own focusable element so we
   * wrap the label in a `<span>` rather than adding an extra tab stop.
   */
  inline?: boolean;
}

/**
 * Wraps any child element with a tooltip. The trigger wrapper stays a
 * `<span>` with `display: inline-flex` so it lays out naturally next to
 * the value pill / control. The popup itself is portaled to document.body
 * so it can be sized independently of the trigger's narrow width.
 *
 * v7.1 — a small mouseleave delay (LEAVE_DELAY_MS) lets the user move
 * the cursor from the trigger onto the popup without it closing, useful
 * when the tooltip contains links or other interactive content.
 */
const LEAVE_DELAY_MS = 120;

export function Tooltip(props: TooltipProps) {
  const { children, value, side = 'top', maxWidth = 260, inline = true } = props;
  const { lang } = useLanguage();
  const id = useId();
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLSpanElement | null>(null);
  const popupRef = useRef<HTMLSpanElement | null>(null);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
  const leaveTimerRef = useRef<number | null>(null);

  const text = pick(lang, value);

  const clearLeaveTimer = () => {
    if (leaveTimerRef.current !== null) {
      window.clearTimeout(leaveTimerRef.current);
      leaveTimerRef.current = null;
    }
  };

  const handleShow = () => {
    clearLeaveTimer();
    setOpen(true);
  };
  const handleHide = () => {
    clearLeaveTimer();
    leaveTimerRef.current = window.setTimeout(() => {
      setOpen(false);
      leaveTimerRef.current = null;
    }, LEAVE_DELAY_MS);
  };

  // When inline=false the trigger is focusable (e.g. a bare icon button).
  // Otherwise we wrap the child without adding an extra tab stop and rely
  // on the child already being focusable (label/input/button).
  const Wrapper = inline ? 'span' : 'span';

  // v7.1 — compute popup position against the trigger rect using
  // useLayoutEffect so the popup is placed before the browser paints.
  // We also re-position on scroll/resize while the tooltip is open so it
  // stays glued to its trigger.
  useLayoutEffect(() => {
    if (!open) {
      setPosition(null);
      return;
    }
    const triggerEl = triggerRef.current;
    const popupEl = popupRef.current;
    if (!triggerEl || !popupEl) return;

    const compute = () => {
      const triggerRect = triggerEl.getBoundingClientRect();
      const popupRect = popupEl.getBoundingClientRect();
      if (side === 'right') {
        // To the right of the trigger, vertically centred.
        setPosition({
          top: triggerRect.top + triggerRect.height / 2 - popupRect.height / 2,
          left: triggerRect.left + triggerRect.width + 8,
        });
      } else {
        // Default: above the trigger, horizontally centred, 8px gap.
        setPosition({
          top: triggerRect.top - popupRect.height - 8,
          left: triggerRect.left + triggerRect.width / 2 - popupRect.width / 2,
        });
      }
    };

    compute();

    const handleReflow = () => compute();
    window.addEventListener('scroll', handleReflow, true);
    window.addEventListener('resize', handleReflow);
    return () => {
      window.removeEventListener('scroll', handleReflow, true);
      window.removeEventListener('resize', handleReflow);
    };
  }, [open, side, text]);

  // v7.1 — clean up the leave timer on unmount so we don't setState on a
  // dead component if the user hovers off just before the page navigates.
  useEffect(() => {
    return () => clearLeaveTimer();
  }, []);

  return (
    <Wrapper
      ref={triggerRef}
      className="relative inline-flex"
      onMouseEnter={handleShow}
      onMouseLeave={handleHide}
      onFocus={handleShow}
      onBlur={handleHide}
      // aria-describedby is set via cloneElement below — too awkward when
      // children is a string, so we set it as a data-attribute and let
      // the child pick it up if it wants to. For our use cases the
      // native `title` fallback (below) is sufficient.
    >
      {/* Native title fallback so non-visual AT still hears the tooltip
          even when focus/hover aren't fired (e.g. touch-screen taps). */}
      <span
        // We render an invisible span carrying the `title` so the popup
        // overlay doesn't inherit it (avoids duplicate tooltip text).
        className="contents"
        title={text}
      >
        {children}
      </span>
      {open && typeof document !== 'undefined'
        ? createPortal(
            <span
              ref={popupRef}
              role="tooltip"
              id={id}
              className={[
                'pointer-events-none fixed z-50 min-w-[200px] max-w-[260px] whitespace-normal rounded-md border border-neutral-300 bg-neutral-100 px-3 py-2 text-xs leading-snug text-neutral-900 shadow-lg motion-safe:transition-opacity',
                side === 'right' ? '' : '',
                'dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-50',
              ].join(' ')}
              style={{
                maxWidth,
                // Position is set after the popup has measured itself; if
                // we haven't computed it yet the popup sits offscreen so
                // the user never sees it at (0,0).
                top: position ? position.top : -9999,
                left: position ? position.left : -9999,
              }}
              onMouseEnter={clearLeaveTimer}
              onMouseLeave={handleHide}
            >
              {text}
            </span>,
            document.body,
          )
        : null}
    </Wrapper>
  );
}